<template>
  <div data-locator="empty-state" class="empty-state">
    <template v-if="translationKeyPrefix">
      <div class="d-flex flex-column align-center justify-center row-gap-2">
        <h2
          class="text--large text--bold text-text-primary"
          data-locator="empty-state-title"
        >
          {{ $t(displayedTitle) }}
        </h2>
        <i18n-t
          :keypath="displayedText"
          class="text-text-secondary"
          tag="span"
          scope="global"
          data-locator="empty-state-text"
        >
          <template #highlight>
            <span class="text--bold">{{ $t(`${translationKeyPrefix}.emptyState.textHighlight`) }}</span>
          </template>
        </i18n-t>
        <slot></slot>
      </div>
    </template>
    <img :src="localizedImageUrl" alt="" v-if="localizedImageUrl" :style="imageStyle" />
  </div>
</template>

<script lang="ts">
import { defineComponent, type PropType } from 'vue'
import { mapGetters } from 'vuex'

const allEmptyStateImages = import.meta.glob('@/assets/img/**/empty_state_*.(png|svg)', { eager: true })

interface LocalizedImages {
  [locale: string]: string
}

type EmptyStateSize = 'sm' | 'md' | 'lg'

export default defineComponent({
  name: 'AppEmptyState',
  props: {
    forceDefaultTranslation: {
      type: Boolean,
    },
    module: {
      type: String,
      required: false,
    },
    size: {
      type: String as PropType<EmptyStateSize>,
      default: 'md',
    },
    translationKeyPrefix: {
      type: String,
      required: false,
    },
  },
  computed: {
    ...mapGetters('room', ['isCurrentUserPm']),
    displayedText(): string {
      return this.getTranslationKey('text')
    },
    displayedTitle(): string {
      return this.getTranslationKey('title')
    },
    imageStyle(): string {
      return this.size === 'sm' ? 'max-width: 368px;' : ''
    },
    localizedImages(): LocalizedImages {
      if (!this.module) return {}

      const folderImages: LocalizedImages = {}
      const modulePattern = `/${this.module}/`

      for (const [path, module] of Object.entries(allEmptyStateImages)) {
        if (path.includes(modulePattern)) {
          const fileName = path.split('/').pop()
          if (fileName) {
            const locale = fileName.replace('empty_state_', '').replace(/(.png|.svg)/, '')
            folderImages[locale] = (module as any).default
          }
        }
      }

      return folderImages
    },
    localizedImageUrl(): string | null {
      return this.localizedImages[this.$i18n.locale] || this.localizedImages['en'] || null
    },
  },
  methods: {
    getTranslationKey(keyType: string): string {
      const defaultKey = `${this.translationKeyPrefix}.emptyState.${keyType}`
      const guestKey = `${defaultKey}Guests`

      if (this.forceDefaultTranslation || this.isCurrentUserPm) return defaultKey

      return this.$te(guestKey) ? guestKey : defaultKey
    },
  },
})
</script>

<style scoped lang="scss">
@import "vuetify/settings";

.empty-state {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  row-gap: 24px;
  width: 100%;

  @media #{map-get($display-breakpoints, 'sm-and-down')} {
    text-align: center;
  }

  img {
    width: auto;
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
    object-position: center;

    @media #{map-get($display-breakpoints, 'lg-and-up')} {
      max-width: 1050px;
    }

    @media #{map-get($display-breakpoints, 'sm-and-down')} {
      display: none;
    }
  }
}
</style>
